import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { useSearchParams } from "react-router-dom";

import { UiFlowKey } from "@signalist/shared-types";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components";

import { useAppSelector } from "@/redux/hooks";

import {
	useGetSeenUiFlowsQuery,
	useMarkUiFlowSeenMutation,
} from "@/pages/Private/redux/userUiFlow/userUiFlow.api";
import { userUiFlowSelector } from "@/pages/Private/redux/userUiFlow/userUiFlow.slice";

// The campaign builder's "Valid" prompt links here with `?assignHint=campaign` (see
// CampaignOverview). The coachmark only appears for that flow, on the UNASSIGNED tab.
const ASSIGN_HINT_VALUE = "campaign";

type Placement = "right" | "top";

type Step = "selectLeads" | "openReachOut" | "addToCampaign";

// Each step anchors to a DOM element. The selection cell is the first row's first cell (the checkbox
// is hover-only); the footer "Reach out" button carries a `data-coachmark` hook (see TableFooter)
// and the menu's "Add to campaign" item its own (see ReachOutMenu).
const STEP_CONFIG: Record<Step, { selector: string; placement: Placement }> = {
	selectLeads: {
		selector: "#table-block tbody tr:first-child td:first-child",
		placement: "right",
	},
	openReachOut: { selector: '[data-coachmark="reach-out-trigger"]', placement: "top" },
	addToCampaign: { selector: '[data-coachmark="add-to-campaign"]', placement: "right" },
};

interface Anchor {
	step: Step;
	placement: Placement;
	top: number;
	left: number;
}

interface AssignLeadsCoachmarkProps {
	/** Number of currently selected leads — step 1 advances to step 2 once at least one is selected. */
	selectedCount: number;
}

export const AssignLeadsCoachmark: FunctionComponent<AssignLeadsCoachmarkProps> = ({
	selectedCount,
}) => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [anchor, setAnchor] = useState<Anchor | null>(null);

	// Once the Reach out menu has been opened, the flow is on its last step; when the menu then closes
	// (the user picked "Add to campaign" or dismissed it) we finish instead of looping back.
	const hasOpenedReachOutMenuRef = useRef(false);

	// One-time-per-user gating (persisted in `user_ui_flow`). Trigger the fetch, then read the seen
	// keys from the slice. We only show the flow once and record it the first time it is displayed.
	useGetSeenUiFlowsQuery();
	const { seenKeys, loaded } = useAppSelector(userUiFlowSelector);
	const [markUiFlowSeen] = useMarkUiFlowSeenMutation();
	const markedSeenRef = useRef(false);

	const tab = searchParams.get("tab") ?? "UNASSIGNED";
	const fromCampaign = searchParams.get("assignHint") === ASSIGN_HINT_VALUE && tab === "UNASSIGNED";

	// Latch the start once the user is eligible (came from the builder, seen-keys loaded, not yet
	// seen). Latching means optimistically marking it seen mid-flow does not abort the current run.
	const [started, setStarted] = useState(false);

	useEffect(() => {
		if (fromCampaign && loaded && !seenKeys.includes(UiFlowKey.ASSIGN_LEADS_COACHMARK)) {
			setStarted(true);
		}
	}, [fromCampaign, loaded, seenKeys]);

	const isActive = started && fromCampaign;

	// Record the flow as seen exactly once, so it never shows again for this user.
	const markSeenOnce = useCallback(() => {
		if (markedSeenRef.current) {
			return;
		}

		markedSeenRef.current = true;
		markUiFlowSeen(UiFlowKey.ASSIGN_LEADS_COACHMARK);
	}, [markUiFlowSeen]);

	// Clearing the trigger params hides the coachmark and keeps it from reappearing on refresh. The
	// reach-out campaign picker reads `assignCampaignUuid` when it mounts (on the "Add to campaign"
	// click), before this fires on the next poll, so the preselect still happens.
	const dismiss = useCallback(() => {
		markSeenOnce();
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);

				next.delete("assignHint");
				next.delete("assignCampaignUuid");

				return next;
			},
			{ replace: true }
		);
	}, [setSearchParams, markSeenOnce]);

	// Pick the active step from observable state: the open reach-out menu wins, then (once leads are
	// selected) the Reach out trigger, otherwise the selection cell. `null` = anchor not in the DOM yet.
	const resolveStep = useCallback((): Step | "done" | null => {
		if (document.querySelector(STEP_CONFIG.addToCampaign.selector)) {
			hasOpenedReachOutMenuRef.current = true;

			return "addToCampaign";
		}

		if (hasOpenedReachOutMenuRef.current) {
			return "done";
		}

		if (selectedCount > 0) {
			return document.querySelector(STEP_CONFIG.openReachOut.selector) ? "openReachOut" : null;
		}

		return document.querySelector(STEP_CONFIG.selectLeads.selector) ? "selectLeads" : null;
	}, [selectedCount]);

	// Track the active anchor's position. Table data + the footer render asynchronously, so we poll
	// briefly and keep the bubble pinned on scroll/resize.
	useEffect(() => {
		if (!isActive) {
			setAnchor(null);

			return;
		}

		const update = () => {
			const step = resolveStep();

			if (step === "done") {
				dismiss();

				return;
			}

			const element = step && document.querySelector(STEP_CONFIG[step].selector);

			if (!step || !element) {
				setAnchor(null);

				return;
			}

			const { placement } = STEP_CONFIG[step];
			const rect = element.getBoundingClientRect();

			// The bubble is about to render for the first time → record the flow as seen.
			markSeenOnce();

			setAnchor({
				step,
				placement,
				top: placement === "right" ? rect.top + rect.height / 2 : rect.top - 12,
				left: placement === "right" ? rect.right + 12 : rect.left + rect.width / 2,
			});
		};

		update();

		const interval = window.setInterval(update, 300);

		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.clearInterval(interval);
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [isActive, resolveStep, dismiss, markSeenOnce]);

	if (!isActive || !anchor) {
		return null;
	}

	const isRight = anchor.placement === "right";

	return ReactDOM.createPortal(
		<div
			className={classNames(
				"fixed z-[2100]",
				isRight ? "-translate-y-1/2" : "-translate-x-1/2 -translate-y-full"
			)}
			data-testid="assign-leads-coachmark"
			role="dialog"
			style={{ top: anchor.top, left: anchor.left }}
		>
			{/* Arrow pointing at the anchored element. */}
			<div
				className={classNames(
					"absolute h-3 w-3 rotate-45 bg-brand-light",
					isRight ? "-left-1 top-1/2 -translate-y-1/2" : "-bottom-1 left-1/2 -translate-x-1/2"
				)}
			/>
			<div className="relative flex max-w-[260px] items-start gap-2 rounded-lg bg-brand-light px-3 py-2 text-white shadow-all">
				<span className="text-xs leading-5">{t(`list.assignCoachmark.${anchor.step}`)}</span>
				<button
					aria-label={t("list.assignCoachmark.close")}
					className="mt-0.5 flex cursor-pointer items-center justify-center text-white/80 hover:text-white"
					onClick={dismiss}
				>
					<Icon className="h-3 w-3" icon={faXmark} />
				</button>
			</div>
		</div>,
		document.body
	);
};
